<template>
  <b-modal
    ref="price-history-modal"
    hide-footer
    title="Lịch sử cập nhật giá sản phẩm"
    v-model="showModal"
    size="xl"
    @hidden="onHidden"
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner small></b-spinner>
      <span class="ml-2">Đang tải dữ liệu...</span>
    </div>
    
    <div v-else-if="priceHistory.length === 0" class="text-center py-4">
      <p class="text-muted">Chưa có lịch sử cập nhật nào</p>
    </div>

    <div v-else>
      <div class="row mb-3">
        <div class="col-md-4">
          <b-form-select
            v-model="selectedAction"
            :options="actionOptions"
            size="sm"
            @change="filterHistory"
          >
            <template v-slot:first>
              <option value="">Tất cả hành động</option>
            </template>
          </b-form-select>
        </div>
      </div>

      <div class="table-responsive">
        <b-table
          :items="filteredHistory"
          :fields="historyFields"
          class="table-bordered table-hover"
          small
        >
          <template v-slot:cell(stt)="row">
            <div class="text-center">
              <span>{{ getRowNumber(row.index) }}</span>
            </div>
          </template>
          <template v-slot:cell(action)="row">
            <b-badge
              :variant="getActionVariant(row.item.action)"
              class="font-weight-bold text-white"
            >
              {{ getActionName(row.item.action) }}
            </b-badge>
          </template>

          <template v-slot:cell(storeName)="row">
            <div class="text-left">
              <span>{{ row.item.storeName }}</span>
              <small v-if="row.item.storeType" class="text-muted d-block">
                {{ getStoreTypeName(row.item.storeType) }}
              </small>
            </div>
          </template>

          <template v-slot:cell(creatorName)="row">
            <div class="text-left">
              <span>{{ row.item.creatorName || 'N/A' }}</span>
              <small class="text-muted d-block">
                {{ formatDateTime(row.item.createdAt) }}
              </small>
            </div>
          </template>

          <template v-slot:cell(changes)="row">
            <div class="text-left">
              <div v-if="row.item.action === HISTORY_ACTION_TYPE.CREATE">
                <div v-if="hasValidData(row.item.newValue)">
                  <div v-for="(value, key) in row.item.newValue" :key="key" class="mb-1">
                    <small v-if="isValidField(key)" class="d-block">
                      <strong>{{ getFieldLabel(key) }}: </strong>
                      <span class="text-success">{{ formatFieldValue(key, value) }}</span>
                    </small>
                  </div>
                </div>
                <div v-else class="text-success">
                  <small>Tạo mới cấu hình giá</small>
                </div>
              </div>
              
              <div v-else-if="row.item.action === HISTORY_ACTION_TYPE.DELETE">
                <div v-if="hasValidData(row.item.oldValue)">
                  <div v-for="(value, key) in row.item.oldValue" :key="key" class="mb-1">
                    <small v-if="isValidField(key)" class="d-block">
                      <strong>{{ getFieldLabel(key) }}: </strong>
                      <span class="text-danger">{{ formatFieldValue(key, value) }}</span>
                    </small>
                  </div>
                </div>
                <div v-else class="text-danger">
                  <small>Xóa cấu hình giá</small>
                </div>
              </div>
              
              <div v-else-if="row.item.action === HISTORY_ACTION_TYPE.UPDATE">
                <div v-if="hasValidData(row.item.oldValue)">
                  <div v-for="(oldValue, key) in row.item.oldValue" :key="key" class="mb-1">
                    <small v-if="isValidField(key) && hasFieldChanged(key, oldValue, row.item.newValue)" class="d-block">
                      <strong>{{ getFieldLabel(key) }}:</strong>
                      <span class="text-danger"> {{ formatFieldValue(key, oldValue) }} </span>
                      <i class="fa fa-arrow-right mx-1 " style="font-size: 12px;"></i>
                      <span class="text-success"> {{ formatFieldValue(key, getNewValue(key, oldValue, row.item.newValue)) }} </span>
                    </small>
                  </div>
                </div>
                <div v-if="!hasChanges(row.item)" class="text-muted">
                  <small>Không có thay đổi nào</small>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:cell(description)="row">
            <span>{{ row.item.description || '-' }}</span>
          </template>
        </b-table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalPages * 7"
          :per-page="7"
          :limit="5"
          size="sm"
          @change="onPageChange"
          :disabled="loading"
        ></b-pagination>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <b-button
        variant="secondary"
        size="sm"
        @click="hideModal"
        style="font-weight: 600; width: 70px"
      >
        Đóng
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { convertPrice } from '@/utils/common';
import { getToastInstance } from '@/utils/toastHelper';
import { STORE_TYPE_OPTIONS } from '@/utils/enum-name';
import { HISTORY_ACTION_TYPE } from '@/utils/enum';

export default {
  name: 'ProductPriceHistoryModal',
  props: {
    productId: {
      type: String,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      priceHistory: [],
      filteredHistory: [],
      loading: false,
      selectedAction: '',
      currentPage: 1,
      totalPages: 1,
      hasMoreData: false,
      historyFields: [
        { key: 'stt', label: 'STT', thStyle: { textAlign: 'center', width: '5%' } },
        { key: 'action', label: 'Hành động', thStyle: { textAlign: 'center', width: '10%' } },
        { key: 'storeName', label: 'Cửa hàng', thStyle: { textAlign: 'left', width: '18%' } },
        { key: 'changes', label: 'Thay đổi', thStyle: { textAlign: 'left', width: '32%' } },
        { key: 'creatorName', label: 'Người thực hiện', thStyle: { textAlign: 'left', width: '18%' } },
        { key: 'description', label: 'Mô tả', thStyle: { textAlign: 'left', width: '17%' } }
      ],
      actionOptions: [
        { value: HISTORY_ACTION_TYPE.CREATE, text: 'Tạo mới' },
        { value: HISTORY_ACTION_TYPE.UPDATE, text: 'Cập nhật' },
        { value: HISTORY_ACTION_TYPE.DELETE, text: 'Xóa' }
      ],
      storeTypeOptions: STORE_TYPE_OPTIONS,
      HISTORY_ACTION_TYPE,
      toast: null,
      FIELD_CONFIG: {
        sellingPrice: { label: 'Giá bán lẻ', type: 'price' },
        wholesalePrice: { label: 'Giá bán buôn', type: 'price' },
        listedPrice: { label: 'Giá niêm yết', type: 'price' },
        updateChildProducts: { label: 'Cập nhật sản phẩm con', type: 'boolean' }
      }
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchPriceHistory();
      }
    }
  },
  created() {
    this.toast = getToastInstance();
  },
  methods: {
    convertPrice,
    
    getStoreTypeName(storeType) {
      const storeTypeOption = this.storeTypeOptions.find(
        (option) => option.value === storeType,
      );
      return storeTypeOption ? storeTypeOption.text : '';
    },

    fetchPriceHistory(page = 1) {
      if (!this.productId) return;

      this.loading = true;
      const params = {
        productId: this.productId,
        page: page,
        limit: 7
      };

      if (this.selectedAction) {
        params.action = this.selectedAction;
      }

      ApiService.query(`/product-price-configs/histories`, { params })
        .then((response) => {
          const history = (response.data.data.result || []).map((item) => ({
            ...item,
            storeName: item.store?.name || '',
            storeType: item.store?.storeType || '',
            creatorName: item.creator.employee?.fullName || item.creator?.username || '',
          }));
          
          this.priceHistory = history;
          this.filteredHistory = [...this.priceHistory];
          this.currentPage = page;
          this.totalPages = response.data.data.totalPages || 1;
        })
        .catch((error) => {
          console.error('Error fetching price history:', error);
          this.toast.makeToastFailure('Không thể tải lịch sử cập nhật giá!');
          this.priceHistory = [];
          this.filteredHistory = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    filterHistory() {
      this.currentPage = 1;
      this.fetchPriceHistory(1, false);
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPriceHistory(page, false);
    },

    getActionName(action) {
      const actionMap = {
        [HISTORY_ACTION_TYPE.CREATE]: 'Tạo mới',
        [HISTORY_ACTION_TYPE.UPDATE]: 'Cập nhật',
        [HISTORY_ACTION_TYPE.DELETE]: 'Xóa'
      };
      return actionMap[action] || 'Không xác định';
    },

    getActionVariant(action) {
      const variantMap = {
        [HISTORY_ACTION_TYPE.CREATE]: 'success',
        [HISTORY_ACTION_TYPE.UPDATE]: 'warning',
        [HISTORY_ACTION_TYPE.DELETE]: 'danger'
      };
      return variantMap[action] || 'secondary';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '';
      const date = new Date(dateTime);
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    hideModal() {
      this.$emit('update:show', false);
    },

    onHidden() {
      this.resetFilters();
    },

    resetFilters() {
      this.selectedAction = '';
      this.currentPage = 1;
      this.fetchPriceHistory(1, false);
    },

    hasValidData(data) {
      return data && Object.keys(data).length > 0;
    },

    isValidField(key) {
      return this.FIELD_CONFIG.hasOwnProperty(key);
    },

    getFieldLabel(key) {
      return this.FIELD_CONFIG[key]?.label || key;
    },

    formatFieldValue(key, value) {
      const fieldConfig = this.FIELD_CONFIG[key];
      if (!fieldConfig) return value;

      switch (fieldConfig.type) {
        case 'price':
          return this.convertPrice(value);
        case 'boolean':
          return value ? 'Có' : 'Không';
        default:
          return value;
      }
    },

    hasFieldChanged(key, oldValue, newValue) {
      const newVal = this.getNewValue(key, oldValue, newValue);
      const [normOld, normNew] = this.normalizeForCompare(key, oldValue, newVal);
      return normOld !== normNew;
    },

    getNewValue(key, oldValue, newValue) {
      return newValue && newValue[key] !== undefined ? newValue[key] : oldValue;
    },

    normalizeForCompare(key, oldValue, newValue) {
      const fieldConfig = this.FIELD_CONFIG[key];
      if (!fieldConfig) return [oldValue, newValue];

      switch (fieldConfig.type) {
        case 'price': {
          const toNum = (v) => {
            if (v === null || v === undefined || v === '') return 0;
            const n = Number(v);
            return Number.isNaN(n) ? 0 : n;
          };
          return [toNum(oldValue), toNum(newValue)];
        }
        case 'boolean': {
          const toBool = (v) => (v === 'true' ? true : v === 'false' ? false : Boolean(v));
          return [toBool(oldValue), toBool(newValue)];
        }
        default:
          return [oldValue, newValue];
      }
    },

    hasChanges(item) {
      if (!item.oldValue || !item.newValue) return false;
      
      const keys = Object.keys(item.oldValue);
      return keys.some(key => {
        if (!this.isValidField(key)) return false;
        return this.hasFieldChanged(key, item.oldValue[key], item.newValue);
      });
    },

    getRowNumber(index) {
      return index + 1;
    }
  },
};
</script>
